/**
 * Frontend pricing config — mirrors /app/backend/config/packages.py (v3, Oct 2026).
 *
 * Plans are sold as "finished 15s ads per month" (1 ad = 10 credits).
 * Prices are FIXED per currency — never convert on the fly.
 *   AUD prices include GST. NZD / USD are shown as-is.
 * Annual billing = 10 x monthly (2 months free). Agency is monthly only.
 */
export const ANNUAL_MULTIPLIER = 10;
export const CREDITS_PER_AD = 10;
export const PREMIUM_MULTIPLIER = 3;

export const CREDIT_COSTS = {
  image: 2,
  broll_5s: 5,
  ad_15s: 10,
  ad_30s: 20,
  voiceover: 1,
  voiceover_hd: 2,
};

export const CURRENCIES = {
  aud: { code: "AUD", symbol: "A$", label: "A$ inc GST, billed in AUD", short: "inc GST", region: "AU" },
  nzd: { code: "NZD", symbol: "NZ$", label: "NZ$, billed in NZD", short: "", region: "NZ" },
  usd: { code: "USD", symbol: "US$", label: "US$, billed in USD", short: "", region: "ROW" },
};

export const PACKAGES = [
  {
    id: "free",
    name: "Free",
    prices: { aud: 0, nzd: 0, usd: 0 },
    adsPerMonth: 0,
    creditsPerMonth: 0,
    trialCredits: 30,
    seats: 1,
    annual: false,
    rollover: false,
    cta: "Start free",
    tagline: "Try a real ad before you pay",
    features: [
      "30 free credits: 1 watermarked 15s ad, or images only",
      "No credit card required",
      "Watermarked exports",
      "Community support",
    ],
  },
  {
    id: "starter",
    name: "Starter",
    prices: { aud: 22, nzd: 22, usd: 13 },
    adsPerMonth: 3,
    creditsPerMonth: 30,
    seats: 1,
    annual: true,
    rollover: false,
    cta: "Start with Starter",
    tagline: "A few finished ads a month",
    features: [
      "3 finished 15s ads per month (30 credits)",
      "Product URL to ad in under 2 minutes",
      "AI scripts checked for AU/NZ ad rules",
      "Commercial usage rights",
      "1 seat",
    ],
  },
  {
    id: "creator",
    name: "Creator",
    prices: { aud: 49, nzd: 49, usd: 29 },
    adsPerMonth: 7,
    creditsPerMonth: 70,
    seats: 1,
    annual: true,
    rollover: false,
    cta: "Start with Creator",
    tagline: "A new ad most weeks",
    features: [
      "7 finished 15s ads per month (70 credits)",
      "Product URL to ad in under 2 minutes",
      "AI scripts checked for AU/NZ ad rules",
      "Voiceovers and talking creators included",
      "1 seat · Email support",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    prices: { aud: 99, nzd: 99, usd: 59 },
    adsPerMonth: 15,
    creditsPerMonth: 150,
    seats: 1,
    annual: true,
    rollover: false,
    popular: true,
    cta: "Start with Growth",
    tagline: "Test hooks every week",
    features: [
      "15 finished 15s ads per month (150 credits)",
      "Unlimited AI scripts and hooks",
      "Brand Kit included",
      "A/B testing engine and Creative Score",
      "Priority rendering · 1 seat",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    prices: { aud: 254, nzd: 254, usd: 149 },
    adsPerMonth: 40,
    creditsPerMonth: 400,
    seats: 3,
    annual: true,
    rollover: true,
    cta: "Start with Pro",
    tagline: "For brands running ads daily",
    features: [
      "40 finished 15s ads per month (400 credits)",
      "Unused credits roll over 1 month",
      "Premium creators and studio voiceovers",
      "ROAS dashboard",
      "3 seats",
    ],
  },
  {
    id: "agency",
    name: "Agency",
    prices: { aud: 649, nzd: 649, usd: 379 },
    adsPerMonth: 100,
    creditsPerMonth: 1000,
    seats: 10,
    annual: false,
    rollover: true,
    cta: "Start with Agency",
    tagline: "Many clients, one workspace",
    features: [
      "100 finished 15s ads per month (1,000 credits)",
      "Unused credits roll over 1 month",
      "10 seats with shared workspace",
      "White-label exports",
      "API access · Monthly billing only",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    prices: { aud: null, nzd: null, usd: null },
    adsPerMonth: -1,
    creditsPerMonth: -1,
    seats: -1,
    annual: false,
    rollover: true,
    contactSales: true,
    cta: "Contact us",
    tagline: "Custom volume and contracts",
    features: [
      "Custom ads and credits per contract",
      "Unused credits roll over 1 month",
      "Unlimited seats",
      "Dedicated infrastructure and custom AI models",
    ],
  },
];

export const PAID_PLAN_IDS = ["starter", "creator", "growth", "pro", "agency"];

// Legacy IDs from the first pricing version — Stripe prices are grandfathered.
export const LEGACY_ALIASES = { basic: "creator", standard: "growth" };

export function canonicalPlanId(id) {
  if (!id) return null;
  return LEGACY_ALIASES[id] || id;
}

export function findPackage(id) {
  const canonical = canonicalPlanId(id);
  return PACKAGES.find((p) => p.id === canonical) || PACKAGES[0];
}

/** Fixed-table price in whole currency units. Annual = 10 x monthly; null when not sold that way. */
export function priceFor(pkg, currency = "usd", interval = "month") {
  const monthly = pkg?.prices?.[currency];
  if (monthly === null || monthly === undefined) return null;
  if (interval === "year") return pkg.annual ? monthly * ANNUAL_MULTIPLIER : null;
  return monthly;
}

/** "A$22" / "NZ$49" / "US$13" — no decimals, no FX. */
export function formatMoney(amount, currency = "usd") {
  const c = CURRENCIES[currency] || CURRENCIES.usd;
  if (amount === null || amount === undefined) return "";
  return `${c.symbol}${Number(amount).toLocaleString("en-AU")}`;
}

/** Effective monthly figure when paying annually (what each month works out to). */
export function monthlyEquivalent(pkg, currency, interval) {
  const total = priceFor(pkg, currency, interval);
  if (total === null) return null;
  return interval === "year" ? Math.round((total / 12) * 100) / 100 : total;
}

/** "A$7.33 per finished ad" — monthly price divided by ads per month. */
export function costPerAd(pkg, currency, interval = "month") {
  const monthly = monthlyEquivalent(pkg, currency, interval);
  if (!monthly || !pkg.adsPerMonth || pkg.adsPerMonth < 1) return null;
  const c = CURRENCIES[currency] || CURRENCIES.usd;
  return `${c.symbol}${(monthly / pkg.adsPerMonth).toFixed(2)}`;
}

export function cheapestPaid(currency = "usd") {
  return PACKAGES.find((p) => p.id === "starter");
}
